import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import Gallery from './Gallery';
import { useCustomDialog } from '../../context/DialogContext';

// ─── Mobile Detection Hook ───────────────────────────────────────────────────
const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth < 640);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);
    return isMobile;
};

const BASE = 'https://menu.searchisend.in';

// ─── Item Modal (Add/Edit) ──────────────────────────────────────────────────
const ItemModal = ({ item, categories, token, onClose, onSaved }) => {
    const isEdit = !!item?.item_id;
    const [form, setForm] = useState({
        item_id:        item?.item_id || '',
        name:           item?.name || '',
        category:       item?.category || (categories[0]?.category_name || ''),
        item_type:      item?.item_type || 'veg',
        image_url:      item?.image_url || '',
        description:    item?.description || '',
        is_popular:     item?.is_popular || 0,
        is_recommended: item?.is_recommended || 0,
        sizes:          item?.sizes && item?.sizes.length > 0 ? item.sizes : [{ size_name: 'Regular', price: '' }]
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [showGallery, setShowGallery] = useState(false);

    const handleField = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(f => ({ ...f, [name]: type === 'checkbox' ? (checked ? 1 : 0) : value }));
    };

    const handleSize = (i, field, value) => {
        const sizes = [...form.sizes];
        sizes[i] = { ...sizes[i], [field]: value };
        setForm(f => ({ ...f, sizes }));
    };

    const addSize  = () => setForm(f => ({ ...f, sizes: [...f.sizes, { size_name: '', price: '' }] }));
    const removeSize = (i) => setForm(f => ({ ...f, sizes: f.sizes.filter((_, idx) => idx !== i) }));

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        
        // Final check for required fields
        if (!form.name || !form.category || (isEdit && !form.item_id)) {
            setError('Required fields missing.');
            setSaving(false);
            return;
        }

        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_manage_menu.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ 
                    action: isEdit ? 'update' : 'create', 
                    ...form 
                })
            });
            const data = await res.json();
            if (data.success) { onSaved(); onClose(); }
            else {
                setError(data.message || 'Failed to save.');
                console.error("Backend response:", data);
            }
        } catch (err) { 
            setError('Network error: ' + err.message); 
        }
        finally { setSaving(false); }
    };

    const inp = { width: '100%', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '10px', boxSizing: 'border-box', marginBottom: '15px', fontSize: '0.95em', outline: 'none' };
    const lb  = { display: 'block', fontWeight: '700', marginBottom: '6px', fontSize: '0.85em', color: '#64748b' };

    return ReactDOM.createPortal(
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', backdropFilter: 'blur(4px)' }}>
            <div style={{ backgroundColor: '#fff', borderRadius: '24px', padding: '30px', width: '100%', maxWidth: '550px', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
                    <h2 style={{ margin: 0, fontSize: '1.5em' }}>{isEdit ? 'Update Menu Item' : 'New Menu Item'}</h2>
                    <button onClick={onClose} style={{ background: '#f1f5f9', border: 'none', width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer', color: '#64748b' }}>×</button>
                </div>

                {error && <div style={{ padding: '12px 15px', backgroundColor: '#fef2f2', color: '#991b1b', borderRadius: '12px', marginBottom: '20px', fontSize: '0.9em', fontWeight: '600', border: '1px solid #fee2e2' }}>{error}</div>}
                
                <form onSubmit={handleSave}>
                    
                    {/* Image Section - Professionalized */}
                    <div style={{ textAlign: 'center', marginBottom: '25px', padding: '20px', background: '#f8fafc', borderRadius: '16px', border: '1px dashed #cbd5e1' }}>
                        <div style={{ width: '100px', height: '100px', margin: '0 auto 15px', borderRadius: '16px', overflow: 'hidden', background: '#fff', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {form.image_url ? (
                                <img src={form.image_url} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                                <i className="fas fa-image" style={{ fontSize: '2em', color: '#cbd5e1' }} />
                            )}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                            <button type="button" onClick={() => setShowGallery(true)} style={{ background: 'var(--primary-orange)', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '50px', fontSize: '0.85em', fontWeight: '700', cursor: 'pointer' }}>
                                <i className="fas fa-images" /> {form.image_url ? 'Change Image' : 'Add Image'}
                            </button>
                            {form.image_url && (
                                <button type="button" onClick={() => setForm(f => ({ ...f, image_url: '' }))} style={{ background: '#fff', color: '#ef4444', border: '1px solid #fee2e2', padding: '8px 16px', borderRadius: '50px', fontSize: '0.85em', fontWeight: '700', cursor: 'pointer' }}>
                                    Remove
                                </button>
                            )}
                        </div>
                        <p style={{ margin: '10px 0 0', fontSize: '0.7em', color: '#94a3b8' }}>{form.image_url ? 'Image linked from gallery' : 'No photo added yet'}</p>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: '15px' }}>
                        <div>
                            <label style={lb}>ITEM NAME</label>
                            <input name="name" required value={form.name} onChange={handleField} style={inp} placeholder="e.g. Cheese Pizza" />
                        </div>
                        <div>
                            <label style={lb}>CATEGORY</label>
                            <select name="category" value={form.category} onChange={handleField} style={inp}>
                                {categories.map(c => <option key={c.category_id} value={c.category_name}>{c.category_name}</option>)}
                            </select>
                        </div>
                    </div>

                    <label style={lb}>DESCRIPTION</label>
                    <textarea name="description" value={form.description} onChange={handleField} style={{ ...inp, height: '80px', resize: 'none' }} placeholder="Tell customers about this dish..." />

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: '15px', marginBottom: '20px' }}>
                        <div>
                            <label style={lb}>ITEM TYPE</label>
                            <select name="item_type" value={form.item_type} onChange={handleField} style={inp}>
                                <option value="veg">Veg</option>
                                <option value="non-veg">Non-Veg</option>
                                <option value="addon">Add-on</option>
                            </select>
                        </div>
                        <div style={{ display: 'flex', gap: '15px', alignItems: 'center', paddingTop: '15px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.85em', fontWeight: '600' }}>
                                <input type="checkbox" name="is_popular" checked={form.is_popular === 1} onChange={handleField} /> Popular
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.85em', fontWeight: '600' }}>
                                <input type="checkbox" name="is_recommended" checked={form.is_recommended === 1} onChange={handleField} /> Rec.
                            </label>
                        </div>
                    </div>

                    <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <label style={{ fontWeight: '800', fontSize: '0.9em', color: '#1e293b' }}>SIZES & PRICING</label>
                            <button type="button" onClick={addSize} style={{ border: 'none', background: '#f8fafc', color: 'var(--primary-orange)', padding: '6px 15px', borderRadius: '50px', cursor: 'pointer', fontWeight: '700', fontSize: '0.8em' }}>+ Add Variant</button>
                        </div>
                        {form.sizes.map((s, i) => (
                            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '10px', marginBottom: '10px' }}>
                                <input placeholder="e.g. Medium" value={s.size_name} onChange={e => handleSize(i, 'size_name', e.target.value)} style={{ ...inp, marginBottom: 0 }} />
                                <input type="number" step="0.01" placeholder="Price" value={s.price} onChange={e => handleSize(i, 'price', e.target.value)} style={{ ...inp, marginBottom: 0 }} />
                                <button type="button" onClick={() => removeSize(i)} style={{ color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2em' }}>×</button>
                            </div>
                        ))}
                    </div>

                    <div style={{ display: 'flex', gap: '12px', marginTop: '30px' }}>
                        <button type="submit" disabled={saving} style={{ flex: 2, padding: '15px', backgroundColor: 'var(--primary-orange)', color: '#fff', border: 'none', borderRadius: '50px', fontWeight: '800', cursor: 'pointer', boxShadow: '0 10px 15px -3px rgba(255,102,0,0.3)', opacity: saving ? 0.7 : 1 }}>
                            {saving ? 'Processing...' : (isEdit ? 'SAVE CHANGES' : 'CREATE ITEM')}
                        </button>
                        <button type="button" onClick={onClose} style={{ flex: 1, padding: '15px', backgroundColor: '#fff', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: '50px', fontWeight: '700', cursor: 'pointer' }}>
                            CANCEL
                        </button>
                    </div>
                </form>

                {showGallery && ReactDOM.createPortal(
                    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 10000, display: 'flex', justifyContent: 'center', alignItems: 'center', backdropFilter: 'blur(8px)' }}>
                        <div style={{ width: '90%', maxWidth: '900px', maxHeight: '85vh', overflow: 'auto', backgroundColor: '#fff', borderRadius: '24px', position: 'relative', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)' }}>
                            <div style={{ sticky: 'top', top: 0, background: '#fff', padding: '20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 10 }}>
                                <h3 style={{ margin: 0 }}>Select Image from Gallery</h3>
                                <button type="button" onClick={() => setShowGallery(false)} style={{ background: '#f1f5f9', border: 'none', width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer' }}>×</button>
                            </div>
                            <div style={{ padding: '20px' }}>
                                <Gallery token={token} onSelectImage={(url) => { 
                                    setForm(f => ({ ...f, image_url: url })); 
                                    setShowGallery(false); 
                                }} />
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
            </div>
        </div>,
        document.body
    );
};

// ─── Manage Menu ─────────────────────────────────────────────────────────────
const ManageMenu = ({ token }) => {
    const { alert: customAlert, confirm: customConfirm } = useCustomDialog();
    const isMobile = useIsMobile();
    const [items, setItems]           = useState([]);
    const [categories, setCategories]   = useState([]);
    const [loading, setLoading]       = useState(true);
    const [message, setMessage]       = useState({ text: '', type: '' });
    const [modalItem, setModalItem]     = useState(null); // null = none, true = new, {item} = edit
    const [isCreateModal, setIsCreate]  = useState(false);
    const [aiCredits, setAiCredits]     = useState(0);
    const [showAiHelper, setShowAiHelper] = useState(false);

    const aiFileRef = React.useRef(null);
    const [aiScanning, setAiScanning] = useState(false);
    const [applyingTemplate, setApplyingTemplate] = useState('');

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchMenu(); fetchCategories(); }, []);

    const fetchMenu = async () => {
        try {
            const res  = await fetch(`${BASE}/apimenu/v2/v2_manage_menu.php?action=fetch&t=${Date.now()}`, { headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json();
            if (data.success) {
                setItems(data.items);
                if(data.ai_credits !== undefined) setAiCredits(data.ai_credits);
            }
            else setMessage({ text: 'Failed to load menu items.', type: 'error' });
        } catch { setMessage({ text: 'Failed to load menu items.', type: 'error' }); }
        finally { setLoading(false); }
    };

    const fetchCategories = async () => {
        try {
            const res  = await fetch(`${BASE}/apimenu/v2/v2_categories.php?action=fetch&t=${Date.now()}`, { headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json();
            if (data.success) setCategories(data.categories);
        } catch {}
    };

    // ─── AI Photo Scanner ──────────────────────────────────────────────────
    const handleAIPhotoSelect = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (aiCredits <= 0) {
            await customAlert("No AI Scans remaining. Please contact support or purchase more credits.", "No AI Credits", "warning");
            return;
        }

        setAiScanning(true);
        setMessage({text: 'Uploading image for analysis...', type: 'success'});

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = async () => {
            const base64data = reader.result;
            setMessage({text: 'Analyzing menu layout and extracting dishes...', type: 'success'});
            try {
                const res = await fetch(`${BASE}/apimenu/v2/v2_ai_parser.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ image: base64data })
                });
                const data = await res.json();
                if (data.success) {
                    setMessage({ text: data.message, type: 'success' });
                    fetchMenu(); 
                } else {
                    setMessage({ text: data.message || 'Analysis failed.', type: 'error' });
                }
            } catch (err) {
                setMessage({ text: 'Network Error during scan.', type: 'error' });
            } finally {
                setAiScanning(false);
                if(aiFileRef.current) aiFileRef.current.value = '';
                setTimeout(() => setMessage({text:'', type:''}), 5000);
            }
        };
    };

    // ─── Starter Templates ─────────────────────────────────────────────────
    const TEMPLATES = {
        'Cafe': [
            { id: '1', category: 'Coffee', name: 'Cappuccino', price: '120', item_type: 'veg', description: '' },
            { id: '2', category: 'Coffee', name: 'Espresso', price: '90', item_type: 'veg', description: '' },
            { id: '3', category: 'Cold Beverages', name: 'Iced Latte', price: '150', item_type: 'veg', description: '' },
            { id: '4', category: 'Bakery', name: 'Butter Croissant', price: '110', item_type: 'veg', description: '' },
            { id: '5', category: 'Bakery', name: 'Blueberry Muffin', price: '85', item_type: 'veg', description: '' }
        ],
        'Indian Dhaba': [
            { id: '1', category: 'Starters', name: 'Paneer Tikka', price: '220', item_type: 'veg', description: '' },
            { id: '2', category: 'Starters', name: 'Chicken Kebab', price: '280', item_type: 'non-veg', description: '' },
            { id: '3', category: 'Main Course', name: 'Butter Chicken', price: '350', item_type: 'non-veg', description: '' },
            { id: '4', category: 'Main Course', name: 'Dal Makhani', price: '180', item_type: 'veg', description: '' },
            { id: '5', category: 'Breads', name: 'Garlic Naan', price: '45', item_type: 'veg', description: '' }
        ],
        'Pizzeria': [
            { id: '1', category: 'Pizzas', name: 'Margherita Pizza', price: '199', item_type: 'veg', description: 'Classic cheese and tomato' },
            { id: '2', category: 'Pizzas', name: 'Pepperoni Pizza', price: '349', item_type: 'non-veg', description: '' },
            { id: '3', category: 'Sides', name: 'Garlic Bread', price: '129', item_type: 'veg', description: '' },
            { id: '4', category: 'Sides', name: 'Cheese Dip', price: '49', item_type: 'veg', description: '' },
            { id: '5', category: 'Beverages', name: 'Coke 330ml', price: '60', item_type: 'veg', description: '' }
        ]
    };

    const handleApplyTemplate = async (templateName) => {
        if(!await customConfirm(`Are you sure you want to load the ${templateName} template? This will instantly add default items.`, 'Load Template', 'warning')) return;
        setApplyingTemplate(templateName);
        try {
            const res = await fetch(`${BASE}/apimenu/bulk_upload.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ items: TEMPLATES[templateName] })
            });
            const data = await res.json();
            if (data.success) {
                setMessage({ text: 'Template applied successfully!', type: 'success' });
                fetchMenu();
            } else {
                setMessage({ text: data.message || 'Failed to post template', type: 'error' });
            }
        } catch {
            setMessage({ text: 'Network error applying template', type: 'error' });
        } finally {
            setApplyingTemplate('');
            setTimeout(() => setMessage({ text: '', type: '' }), 4000);
        }
    };


    const [inlineCat, setInlineCat] = useState('');
    const [addingInlineCat, setAddingInlineCat] = useState(false);

    const handleQuickAddCategory = async (catName) => {
        if (!catName.trim()) return false;
        try {
            const res = await fetch(`${BASE}/apimenu/v2/v2_categories.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ action: 'create', category_name: catName.trim() })
            });
            const data = await res.json();
            if (data.success) { await fetchCategories(); return true; }
        } catch {}
        return false;
    };


    const submitInlineCat = async (e) => {
        e.preventDefault();
        setAddingInlineCat(true);
        const success = await handleQuickAddCategory(inlineCat);
        if (success) { 
            setInlineCat(''); 
            setMessage({text: 'Category created successfully!', type: 'success'}); 
        } else { 
            setMessage({text: 'Failed to create category or it already exists.', type: 'error'}); 
        }
        setAddingInlineCat(false);
        setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    };

    const handleDelete = async (itemId) => {
        if (!await customConfirm('Delete this menu item permanently?', 'Delete Item', 'danger')) return;
        try {
            const res  = await fetch(`${BASE}/apimenu/v2/v2_manage_menu.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ action: 'delete', item_id: itemId })
            });
            const data = await res.json();
            if (data.success) {
                setMessage({ text: 'Item deleted.', type: 'success' });
                fetchMenu();
            } else setMessage({ text: data.message, type: 'error' });
        } catch { setMessage({ text: 'Error deleting item.', type: 'error' }); }
        finally { setTimeout(() => setMessage({ text: '', type: '' }), 3000); }
    };

    const groupedItems = items.reduce((acc, item) => {
        if (!acc[item.category]) acc[item.category] = [];
        acc[item.category].push(item);
        return acc;
    }, {});

    const FALLBACK_IMG = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60'%3E%3Crect width='60' height='60' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-size='20' fill='%239ca3af'%3E🍽%3C/text%3E%3C/svg%3E";

    const typeColors = { veg: { bg: '#d4edda', color: '#155724' }, 'non-veg': { bg: '#f8d7da', color: '#721c24' }, addon: { bg: '#e2e3e5', color: '#383d41' } };

    return (
        <div className="admin-card">
            <Link to="/manage_v2_secure_auth/" className="sticky-back-btn">
    <i className="fas fa-arrow-left"></i> Back to Dashboard
</Link>
            <h1 style={{ marginTop: 0 }}>Manage My Menu</h1>

            {/* ── Toolbar: responsive 2-row layout on mobile ── */}
            <div style={{ marginBottom: '24px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                <button 
                    onClick={() => { 
                        setModalItem(null); 
                        setIsCreate(true); 
                    }}
                    style={{ backgroundColor: 'var(--primary-orange)', color: 'white', padding: '10px 22px', border: 'none', borderRadius: '50px', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s', minHeight: '44px', touchAction: 'manipulation', whiteSpace: 'nowrap' }}
                    className="admin-btn"
                >
                    <i className="fas fa-plus" /> {isMobile ? 'Add Item' : 'Add New Item'}
                </button>
                <form onSubmit={submitInlineCat} style={{ display: 'flex', alignItems: 'center', gap: '5px', flex: isMobile ? '1 1 100%' : 'unset' }}>
                    <input 
                        type="text" 
                        placeholder="New Category Name..." 
                        value={inlineCat} 
                        onChange={e => setInlineCat(e.target.value)}
                        required
                        style={{ padding: '9px 15px', borderRadius: '50px', border: '1px solid var(--border-color)', outline: 'none', flex: 1, minWidth: 0, fontSize: '0.9em', color: 'var(--text-color)', background: 'var(--bg-color)' }}
                    />
                    <button type="submit" disabled={addingInlineCat} style={{ background: 'var(--header-actions-bg)', border: '1px solid var(--border-color)', width: '40px', height: '40px', minHeight: '40px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} title="Add Category">
                        <i className="fas fa-check" style={{ color: 'var(--green-accent)' }} />
                    </button>
                </form>
                <div style={{ display: 'flex', gap: '8px', width: isMobile ? '100%' : 'auto' }}>
                    <button 
                        onClick={() => setShowAiHelper(!showAiHelper)} 
                        style={{ flex: isMobile ? 1 : 'unset', backgroundColor: showAiHelper ? 'var(--primary-orange)' : 'var(--header-actions-bg)', color: showAiHelper ? 'white' : 'var(--text-color)', padding: '10px 16px', borderRadius: '50px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px', border: '1px solid var(--border-color)', cursor: 'pointer', transition: 'all 0.3s', minHeight: '44px', whiteSpace: 'nowrap' }}
                    >
                        <i className="fas fa-magic" /> Magic {showAiHelper ? '↑' : ''}
                    </button>
                    <Link to="/manage_v2_secure_auth/bulk-entry" style={{ flex: isMobile ? 1 : 'unset', marginLeft: isMobile ? 0 : 'auto', backgroundColor: 'var(--header-actions-bg)', color: 'var(--text-color)', padding: '10px 16px', borderRadius: '50px', textDecoration: 'none', fontWeight: '600', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px', border: '1px solid var(--border-color)', minHeight: '44px', whiteSpace: 'nowrap' }}>
                        <i className="fas fa-bolt" /> Bulk
                    </Link>
                </div>
            </div>

            <input type="file" accept="image/*" ref={aiFileRef} style={{ display: 'none' }} onChange={handleAIPhotoSelect} />

            {(showAiHelper || (items.length === 0 && !loading)) && (
                <div style={{ marginBottom: '30px', padding: '25px', background: 'var(--bg-color)', border: '1px solid var(--border-color)', borderRadius: '16px', animation: 'fadeIn 0.5s ease' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                        <div>
                            <h2 style={{ margin: '0 0 5px', fontSize: '1.3rem' }}>🪄 Magic Menu Setup</h2>
                            <p style={{ color: 'var(--text-color-secondary)', fontSize: '0.9rem', margin: 0 }}>Speed up your menu creation with AI Scanning or industry templates.</p>
                        </div>
                        {items.length > 0 && <button onClick={() => setShowAiHelper(false)} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '1.2rem', color: 'var(--text-color-secondary)' }}>&times;</button>}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 250px), 1fr))', gap: '20px' }}>
                        
                        {/* AI Scanner Card */}
                        <div style={{ padding: '20px', border: '1px solid var(--border-color)', borderRadius: '12px', background: 'var(--card-bg-color)', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                <h3 style={{ margin: 0, fontSize: '1.1em' }}><i className="fas fa-camera" /> Photo Scanner</h3>
                                <span style={{ background: '#e0e7ff', color: '#4338ca', fontSize: '0.75em', padding: '4px 10px', borderRadius: '50px', fontWeight: 'bold' }}>{aiCredits} Credits</span>
                            </div>
                            <p style={{ fontSize: '0.85em', color: 'var(--text-color-secondary)', marginBottom: '15px' }}>Upload a photo of your printed menu. We'll extract everything and <b>automatically match images</b> from our gallery!</p>
                            <button 
                                onClick={() => aiFileRef.current?.click()} 
                                disabled={aiScanning}
                                style={{ width: '100%', padding: '10px', background: 'var(--primary-orange)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}
                            >
                                {aiScanning ? <><i className="fas fa-spinner fa-spin"/> Analyzing...</> : 'Upload Menu Photo'}
                            </button>
                        </div>

                        {/* Starter Templates Card */}
                        <div style={{ padding: '20px', border: '1px solid var(--border-color)', borderRadius: '12px', background: 'var(--card-bg-color)', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                            <div style={{ marginBottom: '10px' }}>
                                <h3 style={{ margin: 0, fontSize: '1.1em' }}><i className="fas fa-utensils" /> Starter Templates</h3>
                            </div>
                            <p style={{ fontSize: '0.85em', color: 'var(--text-color-secondary)', marginBottom: '15px' }}>Instantly load 15+ standard items for your restaurant type to get started in seconds.</p>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                {Object.keys(TEMPLATES).map(template => (
                                    <button 
                                        key={template}
                                        onClick={() => handleApplyTemplate(template)}
                                        disabled={applyingTemplate !== ''}
                                        style={{ flex: 1, padding: '8px', background: 'var(--header-actions-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', fontWeight: '600', fontSize: '0.85em', cursor: 'pointer', color: 'var(--text-color)' }}
                                    >
                                        {applyingTemplate === template ? '...' : template}
                                    </button>
                                ))}
                            </div>
                        </div>

                    </div>
                </div>
            )}

            {message.text && (
                <div style={{ padding: '12px', borderRadius: '5px', marginBottom: '16px', fontWeight: '500', backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da', color: message.type === 'success' ? '#155724' : '#721c24' }}>{message.text}</div>
            )}

            {loading ? <p>Loading your menu…</p> : (
                Object.keys(groupedItems).map(category => (
                    <div key={category} style={{ marginBottom: '40px' }}>
                        <div style={{ backgroundColor: 'var(--header-actions-bg)', padding: '10px 15px', borderRadius: '5px', fontSize: '1.2em', fontWeight: '700', color: 'var(--text-color-headings)', marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>{category}</span>
                            <span style={{ fontSize: '0.8em', fontWeight: '500', color: 'var(--text-color-secondary)' }}>{groupedItems[category].length} items</span>
                        </div>

                        {/* ── Mobile: card view ── */}
                        {isMobile ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {groupedItems[category].map((item) => {
                                    const tc = typeColors[item.item_type] || typeColors.addon;
                                    return (
                                        <div key={item.item_id} style={{ backgroundColor: 'var(--card-bg-color)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '12px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                                            <img
                                                src={item.image_url || FALLBACK_IMG}
                                                alt={item.name}
                                                style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '10px', flexShrink: 0 }}
                                                onError={e => { e.target.src = FALLBACK_IMG; }}
                                            />
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontWeight: '700', fontSize: '0.95rem', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                                                <div style={{ fontSize: '0.8em', color: 'var(--text-color-secondary)', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.description || 'No description'}</div>
                                                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                                                    <span style={{ padding: '2px 8px', borderRadius: '50px', fontSize: '0.7em', fontWeight: '700', backgroundColor: tc.bg, color: tc.color }}>{item.item_type?.toUpperCase()}</span>
                                                    {item.sizes?.length > 0 && <span style={{ fontSize: '0.82em', color: 'var(--primary-orange)', fontWeight: '600' }}>₹{item.sizes[0].price}</span>}
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flexShrink: 0 }}>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setModalItem(item); setIsCreate(false); }}
                                                    style={{ background: 'var(--light-orange)', color: 'var(--primary-orange)', border: 'none', borderRadius: '8px', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '1rem' }}
                                                    title="Edit"
                                                ><i className="fas fa-edit" /></button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleDelete(item.item_id); }}
                                                    style={{ background: '#fef2f2', color: 'var(--red-accent)', border: 'none', borderRadius: '8px', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '1rem' }}
                                                    title="Delete"
                                                ><i className="fas fa-trash" /></button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                        /* ── Desktop: table view ── */
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ backgroundColor: 'var(--header-actions-bg)' }}>
                                        {['#', 'Item Details', 'Price', 'Status', 'Actions'].map(h => (
                                            <th key={h} style={{ border: '1px solid var(--border-color)', padding: '10px 12px', textAlign: 'left', fontSize: '0.9em' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {groupedItems[category].map((item, idx) => {
                                        const tc = typeColors[item.item_type] || typeColors.addon;
                                        return (
                                            <tr key={item.item_id} style={{ backgroundColor: idx % 2 === 0 ? 'transparent' : 'var(--bg-color)', transition: 'all 0.2s ease' }} className="table-row-hover">
                                                <td style={{ border: '1px solid var(--border-color)', padding: '10px 12px', color: 'var(--text-color-secondary)', fontSize: '0.85em' }}>{idx + 1}</td>
                                                <td style={{ border: '1px solid var(--border-color)', padding: '8px 12px' }}>
                                                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                                        <img src={item.image_url || FALLBACK_IMG} alt={item.name} style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '6px' }} onError={e => { e.target.src = FALLBACK_IMG; }} />
                                                        <div>
                                                            <div style={{ fontWeight: '700', fontSize: '1rem' }}>{item.name}</div>
                                                            <div style={{ fontSize: '0.8em', color: 'var(--text-color-secondary)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.description || 'No description'}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td style={{ border: '1px solid var(--border-color)', padding: '10px 12px', fontSize: '0.9em' }}>
                                                    {item.sizes?.length > 0 ? item.sizes.map(s => <div key={s.size_name} style={{ margin: '2px 0' }}>{s.size_name}: <strong>₹{s.price}</strong></div>) : <em>No price</em>}
                                                </td>
                                                <td style={{ border: '1px solid var(--border-color)', padding: '10px 12px' }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                        <span style={{ padding: '2px 8px', borderRadius: '50px', fontSize: '0.75em', fontWeight: '700', backgroundColor: tc.bg, color: tc.color, textAlign: 'center' }}>{item.item_type?.toUpperCase()}</span>
                                                        {item.is_popular === 1 && <span style={{ color: '#d97706', fontSize: '0.7em', fontWeight: '700' }}><i className="fas fa-fire" /> POPULAR</span>}
                                                        {item.is_recommended === 1 && <span style={{ color: '#4338ca', fontSize: '0.7em', fontWeight: '700' }}><i className="fas fa-thumbs-up" /> RECOMMENDED</span>}
                                                    </div>
                                                </td>
                                                <td style={{ border: '1px solid var(--border-color)', padding: '10px 12px' }}>
                                                    <div style={{ display: 'flex', gap: '15px' }}>
                                                        <button onClick={(e) => { e.stopPropagation(); setModalItem(item); setIsCreate(false); }} style={{ color: 'var(--primary-orange)', background: 'none', border: 'none', fontWeight: '600', cursor: 'pointer', fontSize: '1.2rem', padding: '4px' }} title="Edit"><i className="fas fa-edit" /></button>
                                                        <button onClick={(e) => { e.stopPropagation(); handleDelete(item.item_id); }} style={{ color: 'var(--red-accent)', background: 'none', border: 'none', fontWeight: '600', cursor: 'pointer', fontSize: '1.2rem', padding: '4px' }} title="Delete"><i className="fas fa-trash" /></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        )}
                    </div>
                ))
            )}

            {(isCreateModal || modalItem) && (
                <ItemModal
                    item={modalItem}
                    categories={categories}
                    token={token}
                    onClose={() => { setModalItem(null); setIsCreate(false); }}
                    onSaved={() => { 
                        fetchMenu(); 
                        setMessage({ text: isCreateModal ? 'Item created successfully!' : 'Item updated successfully!', type: 'success' }); 
                        setTimeout(() => setMessage({ text: '', type: '' }), 3000); 
                    }}
                />
            )}
        </div>
    );
};

export default ManageMenu;
